import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Home, Dumbbell, Plus, Trash2, ArrowRight, Calendar, Edit2, Save, X } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';

const MAX_NAME  = 100;
const MAX_NOTES = 200;
const MAX_REPS  = 9999;
const MAX_WEIGHT = 9999;

const sanitize = (str, max = MAX_NAME) => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, max);

const fmtTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const fmtDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}H ${m}M` : `${m}M`;
};

const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }).toUpperCase();

const calcVolume = (exData) =>
  exData.sets.reduce((t, s) => t + (s.reps || 0) * (s.weight || 0), 0);

const newExercise = () => ({
  id: Date.now(), name: '', sets: 3, repsOrTime: 'reps',
  targetReps: '8-12', timePerSet: 30, restTime: 90, notes: '', isBodyweight: false,
});

// ── WorkoutTimer ──────────────────────────────────────────────

const WorkoutTimer = ({ setCurrentModule }) => {
  const [workouts,       setWorkouts]       = useLocalStorage('workouts',       []);
  const [workoutHistory, setWorkoutHistory] = useLocalStorage('workoutHistory', []);

  const [view,              setView]              = useState('workouts');
  const [currentWorkout,    setCurrentWorkout]    = useState(null);
  const [editingWorkout,    setEditingWorkout]    = useState(null);
  const [sessionData,       setSessionData]       = useState(null);
  const [showResumePrompt,  setShowResumePrompt]  = useState(false);
  const [savedSession,      setSavedSession]      = useState(null);

  // Check for unfinished session on mount
  useEffect(() => {
    const raw = localStorage.getItem('activeWorkoutSession');
    if (!raw) return;
    try {
      setSavedSession(JSON.parse(raw));
      setShowResumePrompt(true);
    } catch {
      localStorage.removeItem('activeWorkoutSession');
    }
  }, []);

  // Persist active session while it's running
  useEffect(() => {
    if (sessionData && view === 'session') {
      try { localStorage.setItem('activeWorkoutSession', JSON.stringify({ ...sessionData, currentWorkout, savedAt: Date.now() })); }
      catch {}
    } else {
      localStorage.removeItem('activeWorkoutSession');
    }
  }, [sessionData, view, currentWorkout]);

  // Resume prompt
  if (showResumePrompt && savedSession) {
    const minutesAgo = Math.floor((Date.now() - savedSession.savedAt) / 60000);
    return (
      <div className="page flex items-center justify-center p-4 sm:p-8">
        <div className="max-w-md w-full card bg-white p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="text-4xl sm:text-5xl mb-4">🔄</div>
            <h2 className="text-2xl sm:text-3xl font-serif text-neutral-900 mb-2 uppercase">Resume?</h2>
            <p className="text-sm sm:text-base text-neutral-700 font-mono">Unfinished workout from {minutesAgo}min ago</p>
          </div>
          <div className="tip-banner mb-6">
            <div className="text-sm text-neutral-900 font-bold font-mono">{savedSession.workoutName}</div>
            <div className="text-sm text-neutral-700 font-mono">Exercise {savedSession.currentExerciseIndex + 1} · Set {savedSession.currentSet + 1}</div>
          </div>
          <div className="space-y-3">
            <button onClick={() => { setCurrentWorkout(savedSession.currentWorkout); setSessionData(savedSession); setView('session'); setShowResumePrompt(false); }} className="w-full bg-neutral-900 text-white py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
              Resume Workout
            </button>
            <button onClick={() => { localStorage.removeItem('activeWorkoutSession'); setShowResumePrompt(false); setSavedSession(null); }} className="w-full border-2 border-neutral-900 bg-white text-neutral-900 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">
              Start Fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  const createNewWorkout  = () => { setEditingWorkout({ id: Date.now(), name: 'New Workout', exercises: [] }); setView('builder'); };
  const saveWorkout       = () => {
    const clean = { ...editingWorkout, name: sanitize(editingWorkout.name) };
    setWorkouts(workouts.find(w => w.id === clean.id) ? workouts.map(w => w.id === clean.id ? clean : w) : [...workouts, clean]);
    setView('workouts'); setEditingWorkout(null);
  };

  const startWorkout = (workout) => {
    setCurrentWorkout(workout);
    setSessionData({ workoutId: workout.id, workoutName: workout.name, startTime: Date.now(), currentExerciseIndex: 0, currentSet: 0, exerciseData: workout.exercises.map(ex => ({ exerciseId: ex.id, sets: [] })) });
    setView('session');
  };

  const completeSession = () => {
    setWorkoutHistory([{ ...sessionData, endTime: Date.now(), duration: Math.floor((Date.now() - sessionData.startTime) / 1000) }, ...workoutHistory]);
    localStorage.removeItem('activeWorkoutSession');
    setView('complete');
  };

  if (view === 'workouts') return <WorkoutList workouts={workouts} setCurrentModule={setCurrentModule} createNewWorkout={createNewWorkout} startWorkout={startWorkout} editWorkout={w => { setEditingWorkout(w); setView('builder'); }} deleteWorkout={id => setWorkouts(workouts.filter(w => w.id !== id))} viewHistory={() => setView('history')} />;
  if (view === 'builder')  return <WorkoutBuilder workout={editingWorkout} setWorkout={setEditingWorkout} saveWorkout={saveWorkout} cancel={() => { setEditingWorkout(null); setView('workouts'); }} setCurrentModule={setCurrentModule} />;
  if (view === 'session')  return <WorkoutSession workout={currentWorkout} sessionData={sessionData} setSessionData={setSessionData} completeSession={completeSession} cancel={() => { setView('workouts'); setSessionData(null); }} setCurrentModule={setCurrentModule} />;
  if (view === 'complete') return <SessionComplete sessionData={sessionData} workout={currentWorkout} backToWorkouts={() => { setView('workouts'); setSessionData(null); setCurrentWorkout(null); }} setCurrentModule={setCurrentModule} />;
  if (view === 'history')  return <WorkoutHistory history={workoutHistory} workouts={workouts} back={() => setView('workouts')} setCurrentModule={setCurrentModule} />;
};

// ── WorkoutList ───────────────────────────────────────────────

const WorkoutList = ({ workouts, setCurrentModule, createNewWorkout, startWorkout, editWorkout, deleteWorkout, viewHistory }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  return (
    <div className="page p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6 sm:mb-8">
          <h1 className="page-title mb-2">My Workouts</h1>
          <p className="text-sm sm:text-base lg:text-lg text-neutral-700 font-mono">Track PRs, volume, progress</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button onClick={viewHistory} className="flex items-center justify-center gap-2 border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">
              <Calendar className="w-4 h-4" /> History
            </button>
            <button onClick={createNewWorkout} className="flex items-center justify-center gap-2 bg-neutral-900 text-white px-6 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
              <Plus className="w-4 h-4" /> New Workout
            </button>
          </div>
        </div>

        {workouts.length === 0 ? (
          <div className="card bg-white p-8 sm:p-12 lg:p-16 text-center">
            <Dumbbell className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-900 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-serif text-neutral-900 mb-2 uppercase">No Workouts Yet</h3>
            <p className="text-sm sm:text-base text-neutral-700 mb-6 font-mono">Create your first workout</p>
            <button onClick={createNewWorkout} className="bg-neutral-900 text-white px-6 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
              Create Workout
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {workouts.map(workout => (
              <div key={workout.id} className="bg-white border-2 border-neutral-900 hover-lift-lg">
                <div className="p-4 sm:p-6">
                  <h3 className="text-xl sm:text-2xl font-serif text-neutral-900 mb-3 uppercase">{workout.name}</h3>
                  <p className="text-neutral-700 text-sm mb-4 font-mono font-bold">{workout.exercises.length} EXERCISE{workout.exercises.length !== 1 ? 'S' : ''}</p>
                  <div className="space-y-1 mb-6">
                    {workout.exercises.slice(0, 3).map(ex => (
                      <div key={ex.id} className="text-sm text-neutral-600 flex items-center gap-2 font-mono">
                        <div className="w-2 h-2 bg-neutral-900 border border-neutral-900 flex-shrink-0" />
                        <span className="truncate">{ex.name || 'Unnamed exercise'}</span>
                      </div>
                    ))}
                    {workout.exercises.length > 3 && <div className="text-sm text-neutral-500 font-mono font-bold">+{workout.exercises.length - 3} MORE</div>}
                  </div>
                </div>
                <div className="border-t-2 border-neutral-900 p-3 sm:p-4 flex gap-2 bg-neutral-50">
                  <button onClick={() => startWorkout(workout)} className="flex-1 bg-neutral-900 text-white py-2 text-xs sm:text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all border-2 border-neutral-900 font-mono">Start</button>
                  <button onClick={() => editWorkout(workout)} className="btn-icon px-3 sm:px-4 py-2"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteConfirm(workout.id)} className="btn-icon-danger px-3 sm:px-4 py-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 sm:p-8 z-50">
            <div className="card bg-white p-6 sm:p-8 max-w-md w-full">
              <h3 className="text-xl sm:text-2xl font-serif text-neutral-900 mb-4 uppercase">Delete Workout?</h3>
              <p className="text-sm sm:text-base text-neutral-700 mb-6 font-mono">This cannot be undone.</p>
              <div className="flex gap-4">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 border-2 border-neutral-900 bg-white text-neutral-900 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">Cancel</button>
                <button onClick={() => { deleteWorkout(deleteConfirm); setDeleteConfirm(null); }} className="flex-1 bg-red-600 text-white py-3 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-red-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── WorkoutBuilder ────────────────────────────────────────────

const WorkoutBuilder = ({ workout, setWorkout, saveWorkout, cancel, setCurrentModule }) => {
  const addExercise    = () => setWorkout({ ...workout, exercises: [...workout.exercises, newExercise()] });
  const deleteExercise = (id) => setWorkout({ ...workout, exercises: workout.exercises.filter(ex => ex.id !== id) });
  const updateExercise = (id, field, value) => setWorkout({ ...workout, exercises: workout.exercises.map(ex => ex.id === id ? { ...ex, [field]: value } : ex) });

  return (
    <div className="page p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6 sm:mb-8">
          <input
            type="text" value={workout.name} maxLength={MAX_NAME}
            onChange={e => setWorkout({ ...workout, name: e.target.value })}
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-neutral-900 bg-transparent border-b-4 border-neutral-900 focus:outline-none mb-4 w-full uppercase font-bold"
            placeholder="WORKOUT NAME"
          />
          <p className="text-sm sm:text-base text-neutral-700 font-mono uppercase tracking-wide">Build your routine</p>
        </div>

        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <h2 className="section-title">Exercises</h2>
            <button onClick={addExercise} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-neutral-900 text-white px-4 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
              <Plus className="w-4 h-4" /> Add Exercise
            </button>
          </div>

          {workout.exercises.length === 0 ? (
            <div className="card bg-white p-8 sm:p-12 text-center">
              <Dumbbell className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-900 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-neutral-700 font-mono uppercase">Add exercises to workout</p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {workout.exercises.map((ex, idx) => (
                <div key={ex.id} className="bg-white border-2 border-neutral-900 p-4 sm:p-6 hover-lift-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-neutral-900 text-white px-3 py-2 text-sm font-mono font-bold min-w-[40px] text-center flex-shrink-0 border-2 border-neutral-900">{idx + 1}</div>
                    <div className="flex-1 space-y-4 min-w-0">
                      <input
                        type="text" value={ex.name} maxLength={MAX_NAME}
                        onChange={e => updateExercise(ex.id, 'name', e.target.value)}
                        placeholder="EXERCISE NAME"
                        className="w-full border-b-4 border-neutral-900 bg-transparent px-0 py-2 text-lg sm:text-xl font-bold text-neutral-900 placeholder-neutral-500 focus:outline-none font-mono uppercase"
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Sets</label>
                          <input type="number" min="1" max="99" value={ex.sets} onChange={e => updateExercise(ex.id, 'sets', Math.max(1, parseInt(e.target.value) || 1))} className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-2xl font-bold text-neutral-900 focus:outline-none font-mono" />
                        </div>
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Type</label>
                          <select value={ex.repsOrTime} onChange={e => updateExercise(ex.id, 'repsOrTime', e.target.value)} className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-sm font-bold text-neutral-900 focus:outline-none font-mono uppercase">
                            <option value="reps">REPS</option>
                            <option value="time">TIMED</option>
                          </select>
                        </div>
                      </div>

                      {ex.repsOrTime === 'reps' ? (
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Target Reps</label>
                          <input type="text" value={ex.targetReps} maxLength={10} onChange={e => updateExercise(ex.id, 'targetReps', e.target.value)} placeholder="8-12 or 10" className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-lg font-bold text-neutral-900 placeholder-neutral-500 focus:outline-none font-mono" />
                        </div>
                      ) : (
                        <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                          <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Time/Set (sec)</label>
                          <input type="number" min="5" max="3600" value={ex.timePerSet} onChange={e => updateExercise(ex.id, 'timePerSet', Math.max(5, parseInt(e.target.value) || 30))} className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-lg font-bold text-neutral-900 focus:outline-none font-mono" />
                        </div>
                      )}

                      <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                        <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Rest Time (sec)</label>
                        <input type="number" min="0" max="600" value={ex.restTime} onChange={e => updateExercise(ex.id, 'restTime', Math.max(0, parseInt(e.target.value) || 0))} className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-lg font-bold text-neutral-900 focus:outline-none font-mono" />
                      </div>

                      <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                        <label className="block text-neutral-900 text-xs uppercase tracking-wider mb-2 font-bold font-mono">Notes</label>
                        <input type="text" value={ex.notes} maxLength={MAX_NOTES} onChange={e => updateExercise(ex.id, 'notes', e.target.value)} placeholder="135lbs, focus on depth" className="w-full border-b-2 border-neutral-900 bg-transparent px-0 py-1 text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none font-mono" />
                      </div>

                      <div className="border-2 border-neutral-900 p-3 bg-neutral-50">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={ex.isBodyweight || false} onChange={e => updateExercise(ex.id, 'isBodyweight', e.target.checked)} className="w-5 h-5 border-2 border-neutral-900" />
                          <span className="text-xs sm:text-sm text-neutral-900 font-mono font-bold uppercase">Bodyweight</span>
                        </label>
                      </div>
                    </div>
                    <button onClick={() => deleteExercise(ex.id)} className="text-white bg-red-600 hover:bg-red-700 transition-colors p-2 flex-shrink-0 border-2 border-neutral-900">
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button onClick={cancel} className="flex-1 border-2 border-neutral-900 bg-white text-neutral-900 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">Cancel</button>
          <button onClick={saveWorkout} className="flex-1 bg-neutral-900 text-white py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
            <Save className="w-4 h-4" /> Save Workout
          </button>
        </div>
      </div>
    </div>
  );
};

// ── WorkoutSession ────────────────────────────────────────────

const WorkoutSession = ({ workout, sessionData, setSessionData, completeSession, cancel }) => {
  const [phase,     setPhase]     = useState('exercise');
  const [timeLeft,  setTimeLeft]  = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef(null);

  const currentExercise     = workout.exercises[sessionData.currentExerciseIndex];
  const currentExerciseData = sessionData.exerciseData[sessionData.currentExerciseIndex];
  const progress = ((sessionData.currentExerciseIndex * 100 + ((sessionData.currentSet + 1) / currentExercise.sets) * 100) / workout.exercises.length);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => setTimeLeft(t => t - 1), 1000);
    } else if (timeLeft === 0 && isRunning) {
      setIsRunning(false);
      if (phase === 'exercise' && currentExercise.repsOrTime === 'time') handleSetComplete();
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, timeLeft]);

  const handleSetComplete = (reps = null, weight = null) => {
    const setData = { setNumber: sessionData.currentSet + 1, reps: reps || (currentExercise.repsOrTime === 'time' ? null : 0), weight: weight || 0, timestamp: Date.now() };
    const newExData = [...sessionData.exerciseData];
    newExData[sessionData.currentExerciseIndex].sets.push(setData);

    if (sessionData.currentSet + 1 < currentExercise.sets) {
      setSessionData({ ...sessionData, currentSet: sessionData.currentSet + 1, exerciseData: newExData });
      if (currentExercise.restTime > 0) { setPhase('rest'); setTimeLeft(currentExercise.restTime); setIsRunning(true); }
    } else if (sessionData.currentExerciseIndex + 1 < workout.exercises.length) {
      const next = workout.exercises[sessionData.currentExerciseIndex + 1];
      setSessionData({ ...sessionData, currentExerciseIndex: sessionData.currentExerciseIndex + 1, currentSet: 0, exerciseData: newExData });
      if (currentExercise.restTime > 0) { setPhase('rest'); setTimeLeft(currentExercise.restTime); setIsRunning(true); }
      else { setPhase('exercise'); setTimeLeft(next.repsOrTime === 'time' ? next.timePerSet : 0); setIsRunning(false); }
    } else {
      setSessionData({ ...sessionData, exerciseData: newExData });
      completeSession();
    }
  };

  const ProgressBar = () => (
    <div className="bg-neutral-700 h-2 overflow-hidden border-2 border-white">
      <div className="bg-white h-full transition-all duration-500" style={{ width: `${progress}%` }} />
    </div>
  );

  if (phase === 'rest') {
    return (
      <div className="page p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="card bg-white">
            <div className="border-b-2 border-neutral-900 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-neutral-900">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-serif text-white uppercase">☕ Rest</h2>
                <button onClick={cancel} className="text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-neutral-900 text-xs sm:text-sm uppercase tracking-wider transition-colors font-mono font-bold">End</button>
              </div>
              <ProgressBar />
            </div>
            <div className="p-6 sm:p-8 lg:p-12 text-center">
              <div className="text-7xl sm:text-8xl lg:text-9xl font-bold text-neutral-900 mb-6 sm:mb-8 font-mono">{fmtTime(timeLeft)}</div>
              <p className="text-sm sm:text-base text-neutral-700 mb-6 sm:mb-8 font-mono uppercase">Next: {workout.exercises[sessionData.currentExerciseIndex].name} · Set {sessionData.currentSet + 1}</p>
              <button onClick={() => { setIsRunning(false); setPhase('exercise'); const next = workout.exercises[sessionData.currentExerciseIndex]; setTimeLeft(next.repsOrTime === 'time' ? next.timePerSet : 0); }} className="bg-neutral-900 text-white px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
                Skip Rest
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="card bg-white">
          <div className="border-b-2 border-neutral-900 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-neutral-900">
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0 flex-1 pr-4">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif text-white mb-1 truncate uppercase">{currentExercise.name}</h2>
                <p className="text-sm sm:text-base text-neutral-300 font-mono font-bold">SET {sessionData.currentSet + 1} OF {currentExercise.sets}</p>
              </div>
              <button onClick={cancel} className="text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-neutral-900 text-xs sm:text-sm uppercase tracking-wider transition-colors flex-shrink-0 font-mono font-bold">End</button>
            </div>
            <ProgressBar />
          </div>

          <div className="p-6 sm:p-8 lg:p-12">
            {currentExercise.repsOrTime === 'time' ? (
              <div className="text-center mb-6 sm:mb-8">
                <div className="text-7xl sm:text-8xl lg:text-9xl font-bold text-neutral-900 mb-4 sm:mb-6 font-mono">{fmtTime(timeLeft)}</div>
                <div className="flex justify-center gap-3 sm:gap-4">
                  <button onClick={() => isRunning ? setIsRunning(false) : (timeLeft === 0 ? (setTimeLeft(currentExercise.timePerSet), setIsRunning(true)) : setIsRunning(true))} className="border-2 border-neutral-900 bg-white text-neutral-900 p-4 sm:p-5 hover:bg-neutral-900 hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg">
                    {isRunning ? <Pause className="w-6 h-6 sm:w-7 sm:h-7" /> : <Play className="w-6 h-6 sm:w-7 sm:h-7" />}
                  </button>
                  <button onClick={() => { setIsRunning(false); setTimeLeft(currentExercise.timePerSet); }} className="border-2 border-neutral-900 bg-white text-neutral-900 p-4 sm:p-5 hover:bg-neutral-900 hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg">
                    <RotateCcw className="w-6 h-6 sm:w-7 sm:h-7" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center mb-6 sm:mb-8">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 mb-4 font-mono uppercase">Target: {currentExercise.targetReps} Reps</div>
              </div>
            )}

            <SetLogger onComplete={handleSetComplete} repsOrTime={currentExercise.repsOrTime} isBodyweight={currentExercise.isBodyweight} />

            {currentExercise.notes && (
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t-2 border-neutral-900">
                <p className="text-xs sm:text-sm text-neutral-700 font-mono"><span className="font-bold uppercase">Note:</span> {currentExercise.notes}</p>
              </div>
            )}
          </div>

          <div className="border-t-2 border-neutral-900 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 bg-neutral-50">
            <h3 className="text-xs sm:text-sm uppercase tracking-wider text-neutral-900 mb-2 font-bold font-mono">Previous Sets</h3>
            {currentExerciseData.sets.length === 0 ? (
              <p className="text-neutral-500 text-xs sm:text-sm font-mono">No sets completed yet</p>
            ) : currentExerciseData.sets.map((set, idx) => (
              <div key={idx} className="text-xs sm:text-sm text-neutral-700 font-mono">
                SET {set.setNumber}: {set.reps ? `${set.reps} REPS` : 'COMPLETED'}{set.weight === null ? ' (BW)' : set.weight > 0 ? ` @ ${set.weight}LBS` : ''}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 sm:mt-6 card-sm bg-white p-3 sm:p-4">
          <h3 className="text-xs sm:text-sm uppercase tracking-wider text-neutral-900 mb-2 sm:mb-3 font-bold font-mono">Up Next</h3>
          {workout.exercises.slice(sessionData.currentExerciseIndex + 1, sessionData.currentExerciseIndex + 4).map(ex => (
            <div key={ex.id} className="text-xs sm:text-sm text-neutral-700 truncate font-mono">{ex.name} - {ex.sets} SETS</div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── SetLogger ─────────────────────────────────────────────────

const SetLogger = ({ onComplete, repsOrTime, isBodyweight }) => {
  const [reps,   setReps]   = useState('');
  const [weight, setWeight] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const r = Math.min(parseInt(reps) || 0, MAX_REPS);
    const w = Math.min(parseFloat(weight) || 0, MAX_WEIGHT);
    onComplete(repsOrTime === 'reps' ? r : null, isBodyweight ? null : w);
    setReps(''); setWeight('');
  };

  return (
    <form onSubmit={handleSubmit} className="border-t-2 border-neutral-900 pt-6 sm:pt-8 space-y-4 sm:space-y-6">
      <div className={`grid gap-4 sm:gap-6 ${repsOrTime === 'reps' && !isBodyweight ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {repsOrTime === 'reps' && (
          <div>
            <label className="block text-neutral-900 text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3 font-bold font-mono">Reps Completed</label>
            <input type="number" min="0" max={MAX_REPS} value={reps} onChange={e => setReps(e.target.value)} onKeyPress={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }} placeholder="0" className="w-full border-b-4 border-neutral-900 bg-transparent px-0 py-2 sm:py-3 text-4xl sm:text-5xl font-bold text-neutral-900 text-center focus:outline-none font-mono" autoFocus />
          </div>
        )}
        {!isBodyweight && (
          <div>
            <label className="block text-neutral-900 text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3 font-bold font-mono">Weight (lbs)</label>
            <input type="number" step="0.5" min="0" max={MAX_WEIGHT} value={weight} onChange={e => setWeight(e.target.value)} onKeyPress={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }} placeholder="0" className="w-full border-b-4 border-neutral-900 bg-transparent px-0 py-2 sm:py-3 text-4xl sm:text-5xl font-bold text-neutral-900 text-center focus:outline-none font-mono" />
          </div>
        )}
      </div>
      <button type="submit" className="w-full bg-neutral-900 text-white py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
        Complete Set <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-center text-neutral-700 text-xs sm:text-sm font-mono">
        Press <kbd className="px-2 py-1 bg-neutral-200 border-2 border-neutral-900 text-xs font-mono font-bold">SPACE</kbd> or <kbd className="px-2 py-1 bg-neutral-200 border-2 border-neutral-900 text-xs font-mono font-bold">ENTER</kbd>
      </p>
    </form>
  );
};

// ── SessionComplete ───────────────────────────────────────────

const SessionComplete = ({ sessionData, workout, backToWorkouts, setCurrentModule }) => {
  const duration = Math.floor((sessionData.endTime - sessionData.startTime) / 1000);
  const prs = sessionData.exerciseData
    .map((exData, idx) => {
      const ex = workout.exercises[idx];
      const maxWeight = Math.max(...exData.sets.map(s => s.weight || 0));
      const maxReps   = Math.max(...exData.sets.map(s => s.reps   || 0));
      const volume    = calcVolume(exData);
      if (!maxWeight && !maxReps && !volume) return null;
      return { exercise: ex.name, maxWeight, maxReps, totalVolume: volume };
    })
    .filter(Boolean);

  return (
    <div className="page flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl w-full">
        <div className="card bg-white p-6 sm:p-8 lg:p-12">
          <div className="text-center mb-6 sm:mb-8">
            <div className="text-5xl sm:text-6xl mb-4 sm:mb-6">💪</div>
            <h1 className="text-3xl sm:text-4xl font-serif text-neutral-900 mb-2 uppercase">Complete!</h1>
            <p className="text-base sm:text-lg text-neutral-900 font-bold font-mono uppercase">{sessionData.workoutName}</p>
            <p className="text-sm sm:text-base text-neutral-700 mt-2 font-mono">Duration: {fmtDuration(duration)}</p>
          </div>

          <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
            {prs.map((pr, idx) => (
              <div key={idx} className="border-2 border-neutral-900 p-4 sm:p-6 bg-neutral-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-3 sm:mb-4 truncate uppercase font-mono">{pr.exercise}</h3>
                <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                  {[['Max Weight', pr.maxWeight], ['Max Reps', pr.maxReps], ['Volume', pr.totalVolume]].map(([label, value]) => (
                    <div key={label} className="border-2 border-neutral-900 p-3 bg-white">
                      <div className="text-2xl sm:text-3xl font-bold text-neutral-900 font-mono">{value}</div>
                      <div className="text-xs uppercase tracking-wider text-neutral-700 mt-1 font-mono font-bold">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 sm:space-y-4">
            <button onClick={backToWorkouts} className="w-full bg-neutral-900 text-white py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">Back to Workouts</button>
            <button onClick={() => setCurrentModule('dashboard')} className="w-full border-2 border-neutral-900 bg-white text-neutral-900 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">Dashboard</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── WorkoutHistory ────────────────────────────────────────────

const WorkoutHistory = ({ history, workouts, back, setCurrentModule }) => (
  <div className="page p-4 sm:p-6 lg:p-8">
    <div className="max-w-6xl mx-auto">
      <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
        <Home className="w-4 h-4" /><span>Dashboard</span>
      </button>

      <div className="mb-6 sm:mb-8">
        <h1 className="page-title mb-2">History</h1>
        <p className="text-sm sm:text-base lg:text-lg text-neutral-700 font-mono font-bold">{history.length} WORKOUT{history.length !== 1 ? 'S' : ''} COMPLETED</p>
        <button onClick={back} className="mt-4 border-2 border-neutral-900 bg-white text-neutral-900 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm uppercase tracking-wider font-bold hover-lift font-mono">← Back</button>
      </div>

      {history.length === 0 ? (
        <div className="card bg-white p-8 sm:p-12 lg:p-16 text-center">
          <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-900 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-serif text-neutral-900 mb-2 uppercase">No History Yet</h3>
          <p className="text-sm sm:text-base text-neutral-700 font-mono">Complete your first workout</p>
        </div>
      ) : (
        <div className="space-y-4">
          {history.map((session, idx) => {
            const workout   = workouts.find(w => w.id === session.workoutId);
            const totalVol  = session.exerciseData.reduce((t, ex) => t + calcVolume(ex), 0);
            const totalSets = session.exerciseData.reduce((t, ex) => t + ex.sets.length, 0);
            const maxWeight = Math.max(...session.exerciseData.flatMap(ex => ex.sets.map(s => s.weight || 0)));

            return (
              <div key={idx} className="bg-white border-2 border-neutral-900 p-4 sm:p-6 hover-lift-lg">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-0 mb-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl sm:text-2xl font-serif text-neutral-900 mb-1 truncate uppercase">{session.workoutName}</h3>
                    <p className="text-neutral-700 text-xs sm:text-sm font-mono font-bold">{fmtDate(session.startTime)}</p>
                  </div>
                  <div className="text-left sm:text-right border-2 border-neutral-900 p-2 bg-neutral-50">
                    <div className="text-base sm:text-lg text-neutral-900 font-bold font-mono">{fmtDuration(session.duration)}</div>
                    <div className="text-neutral-700 text-xs sm:text-sm font-mono uppercase">Duration</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                  {[
                    ['Exercises', session.exerciseData.length],
                    ['Total Sets', totalSets],
                    ['Volume', totalVol],
                    ['Max Weight', maxWeight || 'BW'],
                  ].map(([label, value]) => (
                    <div key={label} className="border-2 border-neutral-900 p-2 sm:p-3 text-center bg-neutral-50">
                      <div className="text-xl sm:text-2xl font-bold text-neutral-900 font-mono">{value}</div>
                      <div className="text-xs uppercase tracking-wider text-neutral-700 mt-1 font-mono font-bold">{label}</div>
                    </div>
                  ))}
                </div>

                <details className="mt-4">
                  <summary className="cursor-pointer text-neutral-900 text-xs sm:text-sm uppercase tracking-wider font-mono font-bold border-2 border-neutral-900 p-2 bg-neutral-50 hover:bg-neutral-100 transition-colors">
                    View Details
                  </summary>
                  <div className="mt-4 space-y-4">
                    {session.exerciseData.map((exData, exIdx) => {
                      const ex = workout?.exercises[exIdx];
                      return (
                        <div key={exIdx} className="border-t-2 border-neutral-900 pt-4">
                          <h4 className="font-bold text-sm sm:text-base text-neutral-900 mb-2 truncate font-mono uppercase">{ex?.name || `EXERCISE ${exIdx + 1}`}</h4>
                          <div className="flex flex-wrap gap-2">
                            {exData.sets.map((set, si) => (
                              <div key={si} className="bg-neutral-900 text-white px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm font-mono font-bold border-2 border-neutral-900">
                                SET {set.setNumber}: {set.reps || 'OK'}{set.weight === null ? ' (BW)' : set.weight > 0 ? ` @ ${set.weight}LBS` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

export default WorkoutTimer;